import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useRef, useState } from 'preact/hooks';
import { X } from 'lucide-preact';
import { EmailSendRuleKind, EmailEventType, EmailOffsetUnit, TouchPointType } from '@/types/touchPoint';
import { assetApi } from '@/api/asset';
import { useEmailRuleLookup } from '@/hooks/useEmailRuleLookup';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { formatEmailRule } from '@/utils/emailRuleFormatter';
import { touchPointsApi } from '@/api/touchPoints';
import { useToast } from '@/hooks/useToast';
import { AssetChipRow } from './touchpoint-sections/AssetChipRow';
import { SentenceRuleEditor } from './touchpoint-sections/SentenceRuleEditor';
const DEFAULT_RULE = {
    Kind: EmailSendRuleKind.Event,
    SendAtAbsolute: null,
    EventType: EmailEventType.WebRegistered,
    OffsetValue: 0,
    OffsetUnit: EmailOffsetUnit.Day,
};
export function EmailActivationPanel({ isOpen, touchPoint, clientCompanyId, workspaceId, campaignId, webId, usedAssetIds, onClose, onSaved, onNavigateToAssets, }) {
    const isEdit = !!touchPoint;
    const { showError, showSuccess } = useToast();
    // ----- State (local to this panel) ---------------------------------------
    const [assetId, setAssetId] = useState(touchPoint?.EmailProperties?.EmailAssetId ?? null);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [rule, setRule] = useState(touchPoint?.EmailProperties?.Rule ?? DEFAULT_RULE);
    const [defaultEmailTypeKey, setDefaultEmailTypeKey] = useState(null);
    const [defaultsApplied, setDefaultsApplied] = useState(false);
    const [saving, setSaving] = useState(false);
    const lookup = useEmailRuleLookup(workspaceId, webId);
    const panelRef = useRef(null);
    // Locks body scroll while open and hides the AI chat widget via
    // the data-asset-overlay CSS rule — matches other overlays' behavior.
    useBodyScrollLock(isOpen);
    // Reset state when the panel reopens for a different touchpoint / create
    useEffect(() => {
        if (!isOpen)
            return;
        setAssetId(touchPoint?.EmailProperties?.EmailAssetId ?? null);
        setRule(touchPoint?.EmailProperties?.Rule ?? DEFAULT_RULE);
        setSelectedAsset(null);
        setDefaultEmailTypeKey(null);
        setDefaultsApplied(false);
    }, [isOpen, touchPoint]);
    // Esc to close + focus management
    useEffect(() => {
        if (!isOpen)
            return;
        const handleEscape = (e) => {
            if (e.key === 'Escape' && !saving)
                onClose();
        };
        document.addEventListener('keydown', handleEscape);
        panelRef.current?.focus();
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, saving, onClose]);
    // ----- Asset selection ---------------------------------------------------
    const onPickAsset = async (asset) => {
        setAssetId(asset.Id);
        setSelectedAsset(asset);
        if (isEdit)
            return; // preserve persisted rule on edit
        // Fetch the asset detail to read EmailTypeKey, then apply the server default
        const detail = await assetApi.getAssetDetail(clientCompanyId, workspaceId, campaignId, asset.Id);
        if (detail.error || !detail.data)
            return;
        const emailTypeKey = detail.data.EmailTypeKey;
        if (!emailTypeKey)
            return;
        const suggested = lookup.data?.DefaultsByEmailTypeKey?.[emailTypeKey];
        if (suggested) {
            setRule(suggested);
            setDefaultEmailTypeKey(emailTypeKey);
            setDefaultsApplied(true);
        }
    };
    const onClearAsset = () => {
        setAssetId(null);
        setSelectedAsset(null);
        setDefaultEmailTypeKey(null);
        setDefaultsApplied(false);
    };
    // Editing the rule clears the "default applied" caption
    const onRuleChange = (next) => {
        setRule(next);
        setDefaultsApplied(false);
    };
    // ----- Save / cancel -----------------------------------------------------
    const canSave = !!assetId && !saving;
    const handleSave = async () => {
        if (!assetId)
            return;
        setSaving(true);
        const name = autoDeriveName(selectedAsset, rule, touchPoint);
        if (isEdit) {
            const res = await touchPointsApi.updateTouchPoint(workspaceId, touchPoint.Id, {
                Name: name,
                ETag: touchPoint.ETag,
                EmailProperties: { EmailAssetId: assetId, Rule: rule },
            });
            setSaving(false);
            if (res.error || !res.data) {
                if (res.status !== 422)
                    showError('Save failed', res.error || 'Unable to save activation');
                return;
            }
            showSuccess('Activation saved', 'The email activation has been updated.');
            onSaved();
            onClose();
        }
        else {
            const res = await touchPointsApi.createTouchPoint(workspaceId, {
                CampaignId: campaignId,
                WebId: webId,
                Name: name,
                Type: TouchPointType.Email,
                EmailProperties: { EmailAssetId: assetId, Rule: rule },
            });
            setSaving(false);
            if (res.error || !res.data) {
                if (res.status !== 422)
                    showError('Create failed', res.error || 'Unable to create activation');
                return;
            }
            showSuccess('Activation created', 'The email activation is configured.');
            onSaved();
            onClose();
        }
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex", role: "dialog", "aria-modal": "true", "aria-labelledby": "email-activation-title", children: [_jsx("button", { type: "button", "aria-label": "Close panel", onClick: onClose, disabled: saving, className: "flex-1 bg-slate-900/20 dark:bg-black/40 transition-opacity" }), _jsxs("aside", { ref: panelRef, tabIndex: -1, className: "w-full sm:w-[520px] md:w-[560px] h-full bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col motion-safe:animate-[slide-in-right_220ms_cubic-bezier(0.22,1,0.36,1)]", children: [_jsxs("header", { className: "flex items-start justify-between gap-3 px-6 pt-6 pb-4 border-b border-slate-200 dark:border-slate-800", children: [_jsxs("div", { children: [_jsx("div", { className: "text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400", children: "Email activation" }), _jsx("h2", { id: "email-activation-title", className: "mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100", children: isEdit ? touchPoint.Name : 'New activation' })] }), _jsx("button", { type: "button", onClick: onClose, disabled: saving, className: "text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded p-1 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1", children: _jsx(X, { className: "w-5 h-5" }) })] }), _jsxs("div", { className: "flex-1 overflow-y-auto px-6 py-6 space-y-8", children: [_jsxs("section", { className: "space-y-3", children: [_jsx(SectionLabel, { children: "Pick an email" }), _jsx(AssetChipRow, { clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, webId: webId, selectedAssetId: assetId, usedAssetIds: usedAssetIds, onSelect: onPickAsset, onClear: onClearAsset, onNavigateToAssets: onNavigateToAssets })] }), assetId && (_jsxs("section", { className: "space-y-4", children: [_jsx(SectionLabel, { children: "When it sends" }), _jsx(SentenceRuleEditor, { value: rule, onChange: onRuleChange, lookup: lookup.data, isLoading: lookup.isLoading }), defaultsApplied && defaultEmailTypeKey && (_jsxs("p", { className: "text-xs text-slate-500 dark:text-slate-400", children: ["Default for", ' ', _jsx("code", { className: "px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300", children: defaultEmailTypeKey }), ' ', "\u2014 edit any underlined token to override."] }))] })), assetId && (_jsxs("section", { className: "rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 px-4 py-3", children: [_jsx("div", { className: "text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400", children: "Will fire" }), _jsx("div", { className: "mt-1 text-sm text-slate-800 dark:text-slate-200", children: formatEmailRule(rule) })] }))] }), _jsxs("footer", { className: "flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60", children: [_jsx("button", { type: "button", onClick: onClose, disabled: saving, className: "px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-slate-50 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:opacity-50", children: "Cancel" }), _jsx("button", { type: "button", onClick: handleSave, disabled: !canSave, className: "inline-flex items-center px-3.5 py-1.5 text-sm font-medium rounded bg-primary-600 hover:bg-primary-700 text-white shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed", children: saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create activation' })] })] })] }));
}
function SectionLabel({ children }) {
    return (_jsx("div", { className: "text-[11px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400", children: children }));
}
function autoDeriveName(asset, rule, touchPoint) {
    if (touchPoint?.Name && touchPoint.Name.trim().length > 0) {
        return touchPoint.Name;
    }
    const base = asset?.Name?.trim() || 'Email activation';
    return `${base} — ${formatEmailRule(rule)}`;
}
